import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from "react";

export type RightPanel = "ai" | "matter-action-bar";

type RightPanelContextValue = {
  activePanel: RightPanel | null;
  isPanelOpen: (panel: RightPanel) => boolean;
  openPanel: (panel: RightPanel) => void;
  closePanel: (panel?: RightPanel) => void;
  togglePanel: (panel: RightPanel) => void;
};

const RightPanelContext = createContext<RightPanelContextValue | null>(null);

export function RightPanelProvider({ children }: { children: ReactNode }) {
  const [activePanel, setActivePanel] = useState<RightPanel | null>(null);

  const isPanelOpen = useCallback(
    (panel: RightPanel) => activePanel === panel,
    [activePanel],
  );

  const openPanel = useCallback((panel: RightPanel) => {
    setActivePanel(panel);
  }, []);

  const closePanel = useCallback((panel?: RightPanel) => {
    setActivePanel((current) => {
      if (panel === undefined) {
        return null;
      }

      return current === panel ? null : current;
    });
  }, []);

  const togglePanel = useCallback((panel: RightPanel) => {
    setActivePanel((current) => (current === panel ? null : panel));
  }, []);

  const value = useMemo(
    () => ({
      activePanel,
      isPanelOpen,
      openPanel,
      closePanel,
      togglePanel,
    }),
    [activePanel, closePanel, isPanelOpen, openPanel, togglePanel],
  );

  return (
    <RightPanelContext.Provider value={value}>
      {children}
    </RightPanelContext.Provider>
  );
}

export function useRightPanel() {
  const context = useContext(RightPanelContext);

  if (!context) {
    throw new Error("useRightPanel must be used within a RightPanelProvider");
  }

  return context;
}
